import type { GridState } from 'ag-grid-community';
import type { BlotterColumnState } from './types';

/** Converts ag-grid's `GridState` column-related sections into the persisted flat blotter column model. */
export function gridStateToBlotterColumnState(gridState: GridState): BlotterColumnState[] {
  const columnStatesById = new Map<string, BlotterColumnState>();
  const orderedIds: string[] = [];

  function getOrCreateColumnState(id: string): BlotterColumnState {
    let columnState = columnStatesById.get(id);
    if (columnState == null) {
      columnState = { id };
      columnStatesById.set(id, columnState);
      orderedIds.push(id);
    }
    return columnState;
  }

  gridState.columnOrder?.orderedColIds.forEach(id => {
    getOrCreateColumnState(id);
  });

  gridState.columnVisibility?.hiddenColIds.forEach(id => {
    getOrCreateColumnState(id).hide = true;
  });

  gridState.columnSizing?.columnSizingModel.forEach(({ colId, width }) => {
    getOrCreateColumnState(colId).width = width;
  });

  gridState.columnPinning?.leftColIds.forEach(id => {
    getOrCreateColumnState(id).pinned = 'left';
  });
  gridState.columnPinning?.rightColIds.forEach(id => {
    getOrCreateColumnState(id).pinned = 'right';
  });

  gridState.rowGroup?.groupColIds.forEach((id, rowGroupIndex) => {
    const columnState = getOrCreateColumnState(id);
    columnState.rowGroup = true;
    columnState.rowGroupIndex = rowGroupIndex;
  });

  gridState.sort?.sortModel.forEach(({ colId, sort }, sortIndex) => {
    const columnState = getOrCreateColumnState(colId);
    columnState.sort = sort === 'asc' ? '+' : sort === 'desc' ? '-' : undefined;
    columnState.sortIndex = sortIndex;
  });

  return orderedIds.map(id => {
    const columnState = columnStatesById.get(id)!;
    return {
      ...columnState,
      hide: columnState.hide ?? false,
      rowGroup: columnState.rowGroup ?? false,
    };
  });
}

/** Converts persisted blotter column state into the partial ag-grid `GridState` shape used for initialization. */
export function blotterColumnStateToGridState(columnStates: BlotterColumnState[]): GridState {
  function sortByIndex<T extends { index?: number }>(items: T[]): T[] {
    return items.toSorted((a, b) => {
      const aIndex = a.index ?? Number.MAX_SAFE_INTEGER;
      const bIndex = b.index ?? Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex;
    });
  }

  return {
    partialColumnState: true,
    columnOrder: {
      orderedColIds: columnStates.map(columnState => columnState.id),
    },
    columnVisibility: {
      hiddenColIds: columnStates.filter(columnState => columnState.hide === true).map(columnState => columnState.id),
    },
    columnSizing: {
      columnSizingModel: columnStates
        .filter((columnState): columnState is BlotterColumnState & { width: number } => columnState.width != null)
        .map(columnState => ({
          colId: columnState.id,
          width: columnState.width,
        })),
    },
    columnPinning: {
      leftColIds: columnStates.filter(columnState => columnState.pinned === 'left').map(columnState => columnState.id),
      rightColIds: columnStates
        .filter(columnState => columnState.pinned === 'right')
        .map(columnState => columnState.id),
    },
    rowGroup: {
      groupColIds: sortByIndex(
        columnStates
          .filter(columnState => columnState.rowGroup === true)
          .map(columnState => ({
            id: columnState.id,
            index: columnState.rowGroupIndex ?? undefined,
          }))
      ).map(columnState => columnState.id),
    },
    sort: {
      sortModel: sortByIndex(
        columnStates
          .filter(columnState => columnState.sort != null)
          .map(
            columnState =>
              ({
                colId: columnState.id,
                sort: columnState.sort === '+' ? 'asc' : 'desc',
                index: columnState.sortIndex ?? undefined,
              }) as const
          )
      ).map(({ colId, sort }) => ({
        colId,
        sort,
      })),
    },
  };
}
